/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Disclaimer from "@/components/Disclaimer";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast, Toaster } from "sonner";
import { CheckCircle2, Sparkles, Loader2, Lock } from "lucide-react";

const TIERS = [
  {
    key: "free",
    name: "Free",
    monthlyPrice: 0,
    annualPrice: 0,
    cta: "Start Free",
    blurb: "Demo + readiness score only",
    features: [
      "Demo Sandbox + Readiness Score number only (no breakdown)",
      "1 fresh AI audit per 30 days",
      "Full report locked",
    ],
    packageMonthly: null,
    packageAnnual: null,
  },
  {
    key: "solo",
    name: "Solo",
    monthlyPrice: 29,
    annualPrice: 24,
    cta: "Choose Solo",
    blurb: "1 store · full 50-state readiness report",
    features: [
      "1 store",
      "Full 50-state readiness report (tax nexus + 15-state privacy scoring)",
      "All 4 pillar scores with explanations",
      "Per-jurisdiction status for all selected states",
      "Readiness Recommendations list",
      "Verified Source reference blocks",
      "Projected score calculator",
      "Policy document scanner (auto-scrape + paste)",
      "\"Why trust this score?\" on every score",
      "2 fresh AI audits per month",
      "Email support — 48hr response",
    ],
    packageMonthly: "solo_monthly",
    packageAnnual: "solo_annual",
  },
  {
    key: "growth",
    name: "Growth",
    monthlyPrice: 59,
    annualPrice: 49,
    cta: "Choose Growth",
    blurb: "Solo + advisor sharing & history",
    features: [
      "Everything in Solo, plus:",
      "PDF export of full readiness report",
      "Shareable report link (unique URL, 30-day expiry)",
      "Score history chart",
      "Monthly compliance digest email",
      "Legal page draft generator (Privacy Policy + ToS)",
      "Priority email support — 24hr response",
      "3 fresh AI audits per month",
    ],
    packageMonthly: "growth_monthly",
    packageAnnual: "growth_annual",
  },
  {
    key: "professional",
    name: "Professional",
    monthlyPrice: 89,
    annualPrice: 74,
    cta: "Choose Professional",
    blurb: "For merchants running 2 Shopify stores",
    badge: "Most popular",
    features: [
      "Everything in Growth, plus:",
      "2 store profiles under one account",
      "Side-by-side readiness dashboard (both stores simultaneously)",
      "6 fresh AI audits per month across both stores",
      "Priority email support — 24hr response",
    ],
    exclusions: [
      "Client management dashboard (Agency only)",
      "Onboarding call (Agency only)",
      "More than 2 store profiles",
    ],
    packageMonthly: "professional_monthly",
    packageAnnual: "professional_annual",
  },
  {
    key: "agency",
    name: "Agency",
    monthlyPrice: 199,
    annualPrice: 165,
    cta: "Choose Agency",
    blurb: "For agencies, accountants, bookkeepers",
    features: [
      "Everything in Professional, plus:",
      "Up to 5 store profiles under one account",
      "Client management dashboard",
      "25 fresh AI audits per month across all stores",
      "30-min onboarding call",
      "Same-day priority support",
    ],
    packageMonthly: "agency_monthly",
    packageAnnual: "agency_annual",
  },
];

export default function Billing() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [busy, setBusy] = useState(null);
  const [trialBusy, setTrialBusy] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [polling, setPolling] = useState(!!sessionId);
  // Manage Subscription — fetch live LS subscription details for the user.
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/payments/subscription")
      .then(({ data }) => setSubscription(data))
      .catch(() => setSubscription(null));
  }, [user]);

  useEffect(() => {
    if (!sessionId || !polling) return;
    let active = true;
    const poll = async (n) => {
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          toast.success(`Welcome to Regulens ${data.tier?.toUpperCase() || "Pro"}`);
          await refresh();
          setPolling(false);
          setTimeout(() => navigate("/dashboard"), 1200);
        } else if (data.status === "expired" || n >= 8) {
          toast.error("Payment session expired");
          setPolling(false);
        } else {
          setTimeout(() => poll(n + 1), 2000);
          setPollAttempts(n + 1);
        }
      } catch {
        if (n < 5) setTimeout(() => poll(n + 1), 2000);
        else setPolling(false);
      }
    };
    poll(0);
    return () => { active = false; };
  }, [sessionId, polling, navigate, refresh]);

  const startCheckout = async (tier) => {
    if (!user) { navigate("/login"); return; }
    if (tier.key === "free") return;
    // Pre-checkout terms gate — payment processors treat acceptance evidence
    // as protective in chargeback disputes. The modal renders globally in
    // App.js and blocks the page until the user accepts.
    try {
      const { data: status } = await api.get("/auth/me/terms-status");
      if (status?.needs_acceptance) {
        toast.error("Please accept the Terms of Service and Refund Policy to continue.");
        window.dispatchEvent(new CustomEvent("regulens:terms-recheck"));
        return;
      }
    } catch {
      /* If the status check fails, fall through — global modal still gates. */
    }
    const pkg = tier.packageMonthly;
    if (!pkg) return;
    setBusy(pkg);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/checkout/session", { origin_url: origin, package: pkg });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const startTrial = async () => {
    if (!user) { navigate("/register"); return; }
    setTrialBusy(true);
    try {
      const { data } = await api.post("/payments/start-trial");
      toast.success(`7-day Growth trial activated — ends ${data.trial_ends_at.slice(0,10)}`);
      await refresh();
      setTimeout(() => navigate("/dashboard"), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trial start failed");
    } finally {
      setTrialBusy(false);
    }
  };

  return (
    <Layout fullWidth>
      <Toaster theme="dark" position="top-right" offset={80} />
      <section className="max-w-7xl mx-auto px-6 py-12">
        {polling && (
          <div className="rounded-md border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] p-4 mb-6 flex items-center gap-3" data-testid="payment-polling">
            <Loader2 className="w-5 h-5 text-[var(--rg-sage)] animate-spin" />
            <div className="text-sm text-[var(--rg-text-strong)]">Verifying your payment... (attempt {pollAttempts + 1})</div>
          </div>
        )}

        <div className="text-center mb-8">
          <Sparkles className="w-10 h-10 text-[var(--rg-sage)] mx-auto" />
          <h1 className="font-heading font-extrabold text-4xl text-[var(--rg-text-strong)] mt-3">Choose your readiness plan</h1>
          <p className="text-[var(--rg-text)] mt-2 max-w-2xl mx-auto">Informational readiness intelligence for U.S. Shopify merchants — sales tax nexus rules for 50 jurisdictions and privacy law scoring for 15 states. Cancel anytime.</p>
        </div>

        {/* 7-day Growth free trial */}
        <div className="rounded-lg border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] p-5 mb-8 text-center" data-testid="free-trial-card">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-1">7-Day Free Trial</div>
          <div className="font-heading font-bold text-xl text-[var(--rg-text-strong)] mb-1">Try Growth free for 7 days</div>
          <p className="text-sm text-[var(--rg-text)] mb-3">Full Growth access. No credit card required to start.</p>
          <Button onClick={startTrial} disabled={trialBusy || user?.tier === "growth" || user?.tier === "agency"} className="bg-[var(--rg-gold)] text-[var(--rg-on-gold)] hover:bg-[var(--rg-gold-hover)] font-semibold" data-testid="start-free-trial-btn">
            {trialBusy ? "Activating..." : user?.tier === "growth" || user?.tier === "agency" ? "Already on a paid tier" : "Start 7-day Free Trial"}
          </Button>
        </div>

        {/* Annual toggle removed — Lemon Squeezy variants are monthly only.
            (Re-introduce when annual variants are configured in LS.) */}

        {/* Manage Subscription (paid tier merchants only) */}
        {subscription && subscription.status && subscription.status !== "inactive" && (
          <div
            className="rounded-lg border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] p-5 mb-8 flex flex-wrap items-center gap-4"
            data-testid="manage-subscription-card"
          >
            <div className="flex-1 min-w-[240px]">
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-1">Active subscription</div>
              <div className="font-heading font-bold text-lg text-[var(--rg-text-strong)]" data-testid="current-plan-name">
                {subscription.product_name || subscription.variant_name || `Plan: ${user?.tier?.toUpperCase()}`}
                <span className="ml-2 text-xs text-[var(--rg-text)] font-mono uppercase">· {subscription.status}</span>
              </div>
              {subscription.renews_at && (
                <div className="text-xs text-[var(--rg-text)] mt-1" data-testid="next-billing-date">
                  Next billing: <span className="font-mono text-[var(--rg-text-strong)]">
                    {new Date(subscription.renews_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                  </span>
                </div>
              )}
              {subscription.cancelled && subscription.ends_at && (
                <div className="text-xs text-[var(--rg-amber)] mt-1" data-testid="cancel-at-period-end">
                  Cancels on <span className="font-mono">{(subscription.ends_at || "").slice(0, 10)}</span>. You retain access until then.
                </div>
              )}
              {subscription.card_brand && subscription.card_last_four && (
                <div className="text-[10px] text-[var(--rg-muted)] font-mono mt-1">
                  {subscription.card_brand} ···· {subscription.card_last_four}
                </div>
              )}
            </div>
            {subscription.customer_portal && (
              <a
                href={subscription.customer_portal}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="manage-subscription-link"
              >
                <Button className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-bold">
                  Manage Subscription
                </Button>
              </a>
            )}
          </div>
        )}

        {/* P11 — Social proof block above pricing tiers */}
        <div className="mb-8 rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 text-center" data-testid="billing-social-proof">
          <h3 className="font-heading font-bold text-lg text-[var(--rg-text-strong)] mb-4">Built for independent Shopify merchants</h3>
          <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div data-testid="billing-stat-jurisdictions">
              <div className="font-heading font-extrabold text-3xl text-[var(--rg-sage)]">39</div>
              <div className="text-xs text-[var(--rg-text)] mt-1">jurisdictions covered</div>
            </div>
            <div data-testid="billing-stat-pillars">
              <div className="font-heading font-extrabold text-3xl text-[var(--rg-sage)]">4</div>
              <div className="text-xs text-[var(--rg-text)] mt-1">pillar scoring system</div>
            </div>
            <div data-testid="billing-stat-sources">
              <div className="font-heading font-extrabold text-3xl text-[var(--rg-sage)]">100%</div>
              <div className="text-xs text-[var(--rg-text)] mt-1">findings cite official government sources</div>
            </div>
          </div>
          <p className="text-xs text-[var(--rg-muted)] mt-5 max-w-3xl mx-auto leading-relaxed">
            Regulens is informational only and does not provide legal advice. All findings trace
            to official government publications.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 items-stretch" data-testid="pricing-grid">
          {TIERS.map((tier) => {
            const price = tier.monthlyPrice;
            const pkg = tier.packageMonthly;
            const isCurrent = user?.tier === tier.key;
            return (
              <div key={tier.key} className={`rounded-lg border bg-[var(--rg-surface)] p-6 flex flex-col h-full ${tier.key === "solo" ? "border-[var(--rg-sage)]" : "border-[var(--rg-border)]"}`} data-testid={`tier-${tier.key}`}>
                {tier.badge && (
                  <div className="inline-flex w-fit px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--rg-sage)] border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] mb-3">{tier.badge}</div>
                )}
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-muted)]">{tier.name}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-heading font-extrabold text-4xl text-[var(--rg-text-strong)]">${price}</span>
                  {price > 0 && <span className="text-sm text-[var(--rg-text)]">/mo</span>}
                </div>
                <div className="text-xs text-[var(--rg-text)] mt-1">{tier.blurb}</div>
                <ul className="space-y-2 mt-5 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-[var(--rg-text)]">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[var(--rg-sage)] flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                  {tier.exclusions && tier.exclusions.length > 0 && (
                    <>
                      <li className="mt-2 text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)] font-bold">Not included</li>
                      {tier.exclusions.map((x) => (
                        <li key={x} className="flex items-start gap-2 text-xs text-[var(--rg-muted)]">
                          <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                          <span>{x}</span>
                        </li>
                      ))}
                    </>
                  )}
                </ul>
                {tier.key === "free" ? (
                  <Button
                    onClick={() => navigate("/register")}
                    disabled={isCurrent}
                    data-testid="upgrade-free-btn"
                    className="mt-6 w-full font-bold bg-[var(--rg-surface)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface-2)] border border-[var(--rg-border)]"
                  >
                    {isCurrent ? "Your current plan" : tier.cta}
                  </Button>
                ) : (
                  <Button
                    onClick={() => startCheckout(tier)}
                    disabled={isCurrent || busy === pkg}
                    data-testid={`upgrade-${tier.key}-btn`}
                    className={`mt-6 w-full font-bold ${tier.key === "professional" ? "bg-[var(--rg-gold)] text-[var(--rg-on-gold)] hover:bg-[var(--rg-gold-hover)]" : "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)]"}`}
                  >
                    {isCurrent ? "Your current plan" : busy === pkg ? "Redirecting..." : tier.cta}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-8"><Disclaimer /></div>
      </section>
    </Layout>
  );
}

export const BillingSuccess = Billing;
